import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import {
  Comment as CommentIcon,
  Send as SendIcon,
  HistoryEdu as RevisionIcon,
} from '@mui/icons-material';
import { documentService } from '../services/documentService';
import { IDocumentFeedback } from '../types/document';
import { stitchColors, stitchFonts } from '../theme/tokens';

export interface DocumentFeedbackSectionProps {
  documentId: string;
  currentRevision?: {
    id: string;
    number: number;
    contentHash: string;
  } | null;
  canSubmitFeedback: boolean; // Estate Owner
  canViewFeedback: boolean; // Estate Owner or Lawyer
  isFinalized?: boolean;
}

export const DocumentFeedbackSection: React.FC<DocumentFeedbackSectionProps> = ({
  documentId,
  currentRevision,
  canSubmitFeedback,
  canViewFeedback,
  isFinalized = false,
}) => {
  const queryClient = useQueryClient();
  const [message, setMessage] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    data: feedbackData,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['documentFeedback', documentId],
    queryFn: () => documentService.getDocumentFeedback(documentId),
    enabled: Boolean(documentId && canViewFeedback),
    refetchOnWindowFocus: true,
  });

  const feedbackList: IDocumentFeedback[] = feedbackData?.feedback || [];

  const feedbackMutation = useMutation({
    mutationFn: (text: string) => {
      if (!currentRevision?.id || !currentRevision?.contentHash) {
        throw new Error('No current revision available to attach feedback to.');
      }
      return documentService.createDocumentFeedback(documentId, {
        revisionId: currentRevision.id,
        contentHash: currentRevision.contentHash,
        message: text,
      });
    },
    onSuccess: () => {
      setMessage('');
      setSubmitError(null);
      queryClient.invalidateQueries({ queryKey: ['documentFeedback', documentId] });
      refetch();
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message || err?.message || 'Failed to submit feedback.';
      setSubmitError(msg);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;
    feedbackMutation.mutate(message.trim());
  };

  if (!canViewFeedback) {
    return null;
  }

  return (
    <Box sx={{ mt: 5, animation: 'floatIn 0.6s ease-out 0.28s both' }}>
      <Divider sx={{ mb: 4, borderColor: stitchColors.outlineVariant }} />

      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CommentIcon sx={{ color: stitchColors.primary }} />
          <Typography
            variant="h5"
            sx={{
              fontFamily: stitchFonts.headline,
              fontWeight: 600,
              color: 'var(--page-ink)',
            }}
          >
            Document Feedback
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ mt: 0.5, color: 'var(--page-muted)' }}>
          Estate Owners and Estate Lawyers can communicate feedback regarding unclear wording or adjustments.
        </Typography>
      </Box>

      {/* Submission Form for Estate Owner */}
      {canSubmitFeedback && !isFinalized && (
        <Paper
          elevation={0}
          component="form"
          onSubmit={handleSubmit}
          sx={{
            p: 2.5,
            mb: 4,
            borderRadius: 2,
            border: `1px solid ${stitchColors.outlineVariant}`,
            bgcolor: stitchColors.surfaceContainerLowest,
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Submit Feedback on Current Revision
            {currentRevision?.number !== undefined && ` (Rev #${currentRevision.number})`}
          </Typography>

          {submitError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 1.5 }}>
              {submitError}
            </Alert>
          )}

          <TextField
            multiline
            rows={3}
            fullWidth
            placeholder="Describe any unclear wording, requested corrections, or questions..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            disabled={feedbackMutation.isPending}
            sx={{ mb: 1.5 }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="submit"
              variant="contained"
              startIcon={feedbackMutation.isPending ? <CircularProgress size={18} /> : <SendIcon />}
              disabled={feedbackMutation.isPending || !message.trim() || !currentRevision?.id}
              sx={{ borderRadius: 2 }}
            >
              {feedbackMutation.isPending ? 'Submitting...' : 'Submit Feedback'}
            </Button>
          </Box>
        </Paper>
      )}

      {/* Feedback Feed */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : isError ? (
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          Unable to load feedback at this time.
        </Alert>
      ) : feedbackList.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            textAlign: 'center',
            borderRadius: 2,
            border: `1px dashed ${stitchColors.outlineVariant}`,
            bgcolor: stitchColors.surfaceContainerLowest,
          }}
        >
          <Typography variant="body2" sx={{ color: 'var(--page-muted)', fontStyle: 'italic' }}>
            No feedback entries yet for this document.
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {feedbackList.map((entry) => {
            const revNumber =
              typeof entry.revisionId === 'object' && entry.revisionId !== null
                ? (entry.revisionId as any).revisionNumber
                : undefined;
            const authorName = entry.authorUserId?.name || entry.authorUserId?.email || 'Estate Owner';
            const isOwner = entry.authorUserId?.userType === 'EO' || entry.authorUserId?.userType === 'ESTATE_OWNER';
            const authorRole = isOwner ? 'Estate Owner' : 'Estate Lawyer';
            const dateStr = new Date(entry.createdAt).toLocaleString('en-AU', {
              dateStyle: 'medium',
              timeStyle: 'short',
            });

            return (
              <Card
                key={entry._id}
                elevation={0}
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${stitchColors.outlineVariant}`,
                  bgcolor: stitchColors.surfaceContainerLowest,
                }}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {authorName}
                      </Typography>
                      <Chip
                        label={authorRole}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.72rem',
                          bgcolor: stitchColors.secondaryContainer,
                          color: stitchColors.onSecondaryFixedVariant,
                        }}
                      />
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {revNumber !== undefined && (
                        <Chip
                          icon={<RevisionIcon fontSize="small" />}
                          label={`Rev #${revNumber}`}
                          size="small"
                          variant="outlined"
                          sx={{ height: 22, fontSize: '0.75rem' }}
                        />
                      )}
                      <Typography variant="caption" sx={{ color: 'var(--page-muted)' }}>
                        {dateStr}
                      </Typography>
                    </Box>
                  </Box>

                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      lineHeight: 1.6,
                      color: stitchColors.onBackground,
                    }}
                  >
                    {entry.message}
                  </Typography>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default DocumentFeedbackSection;
